import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { PipelineRecord } from '../types';

// Super Admin only — the server enforces this too (requireSuperAdmin on
// every /api/pipeline/trash* route), so a non-admin hitting these hooks
// would just get a 403, never real data.

export interface TrashedPipelineRecord extends PipelineRecord {
  trashedAt?: string | null;
  trashedReason?: string;
  trashedBy?: { name?: string; email?: string } | null;
}

const KEY = ['pipeline', 'trash'] as const;

export function usePipelineTrashList() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () =>
      (await api.get<{ items: TrashedPipelineRecord[]; total: number }>('/api/pipeline/trash?limit=200')).data,
  });
}

export function usePipelineTrashMutations() {
  const qc = useQueryClient();
  const invalidate = () => Promise.all([
    qc.invalidateQueries({ queryKey: KEY }),
    qc.invalidateQueries({ queryKey: ['pipeline'] }),
  ]);

  const restore = useMutation({
    mutationFn: async (id: string) => (await api.post(`/api/pipeline/trash/${id}/restore`)).data,
    onSuccess: invalidate,
  });

  const permanentlyDelete = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/pipeline/trash/${id}`)).data,
    onSuccess: invalidate,
  });

  return { restore, permanentlyDelete };
}
